// Generated by TaroBase SDK Generator: 2026-07-24T09:46:28.100Z
// Collection: tokenConfig/$id
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * Singleton (id must be 'main') holding the t-hive token configuration so both frontend and backend can read the mint address, treasury, total supply and a created flag. Written once at bootstrap after the token is created.
 */

export interface TokenConfigRequest {
  mint: AddressType;
  treasury: AddressType;
  totalSupply: number | TimeOperation | IncrementOperation | TokenAmount;
  decimals: number | TimeOperation | IncrementOperation | TokenAmount;
  symbol: string;
  created: boolean;
}

export interface TokenConfigResponse {
  mint: string;
  treasury: string;
  totalSupply: number;
  decimals: number;
  symbol: string;
  created: boolean;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a TokenConfig operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildTokenConfig(id: string, data: TokenConfigRequest): DocumentOperation {
  const doc = transformValues(data);
  return { path: `tokenConfig/${id}`, document: doc };
}

/**
 * Document ID MUST be 'main' (enforced via @constants.THIVE_CONFIG_ID). Only ADMIN_ADDRESS or the backend PROJECT_VAULT_ADDRESS may create it. Written once during bootstrap after the tHiveToken record is created and its mint address is captured. Provide: mint (the captured SPL mint address), treasury (@constants.PROJECT_VAULT_ADDRESS), totalSupply (1000000000000000 base units = 1B * 1e6), decimals (6), symbol ('t-hive'), created (true). (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setTokenConfig(id: string, data: TokenConfigRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`tokenConfig/${id}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting TokenConfig: ${formatError(error)}`);
    return false;
  }
}

export type TokenConfigRequestUpdate = Partial<TokenConfigRequest>;

/**
 * Build a TokenConfig update operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUpdateTokenConfig(id: string, data: TokenConfigRequestUpdate): DocumentOperation {
  const doc = transformValues(data);
  return { path: `tokenConfig/${id}`, document: doc };
}

/**
 * Only ADMIN_ADDRESS or PROJECT_VAULT_ADDRESS may update, and only for id 'main'. Used if the mint address needs to be corrected during bootstrap. (Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the update worked.
 */
export async function updateTokenConfig(id: string, data: TokenConfigRequestUpdate): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`tokenConfig/${id}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error updating TokenConfig: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Anyone can read the t-hive token config (mint address, treasury, total supply, decimals, symbol, created flag).
   (Get Single Item)
 */
export async function getTokenConfig(id: string): Promise<TokenConfigResponse | null> {
  try {
    const result = await get(`tokenConfig/${id}`);
    return result as TokenConfigResponse | null;
  } catch (error) {
    console.error(`Error getting TokenConfig: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single TokenConfig document. (
  Read Operation Details: Anyone can read the t-hive token config (mint address, treasury, total supply, decimals, symbol, created flag).
  )
 */
export function subscribeTokenConfig(callback: (data: TokenConfigResponse | null) => void, id: string): Promise<() => Promise<void>> {
  return subscribe(`tokenConfig/${id}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as TokenConfigResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to TokenConfig at path '${`tokenConfig/${id}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many TokenConfig items from collection tokenConfig
 
  Read Operation Details: Anyone can read the t-hive token config (mint address, treasury, total supply, decimals, symbol, created flag).
  
 */
export async function getManyTokenConfig(filter: string = ""): Promise<TokenConfigResponse[]> {
  try {
    const results = await get("tokenConfig", { prompt: filter });
    return Array.isArray(results) ? results as TokenConfigResponse[] : [];
  } catch (error) {
    console.error(`Error getting TokenConfig collection at path 'tokenConfig': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in TokenConfig collection at tokenConfig
 
  Read Operation Details: Anyone can read the t-hive token config (mint address, treasury, total supply, decimals, symbol, created flag).
  
 */
export function subscribeManyTokenConfig(callback: (data: TokenConfigResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("tokenConfig", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as TokenConfigResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to TokenConfig collection at path '${"tokenConfig"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all TokenConfig items from collection tokenConfig
 
  Read Operation Details: Anyone can read the t-hive token config (mint address, treasury, total supply, decimals, symbol, created flag).
  
 */
export async function getAllTokenConfig(filter: string = ""): Promise<TokenConfigResponse[]> {
  try {
    const results = await get("tokenConfig", { prompt: filter });
    return Array.isArray(results) ? results as TokenConfigResponse[] : [];
  } catch (error) {
    console.error(`Error getting TokenConfig collection at path 'tokenConfig': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in TokenConfig collection at tokenConfig
 
  Read Operation Details: Anyone can read the t-hive token config (mint address, treasury, total supply, decimals, symbol, created flag).
  
 */
export function subscribeAllTokenConfig(callback: (data: TokenConfigResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("tokenConfig", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as TokenConfigResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to TokenConfig collection at path '${"tokenConfig"}': ${formatError(error)}`);
    }
  });
}

/**
 * Count TokenConfig items in collection tokenConfig.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countTokenConfig(filter: string = ""): Promise<AggregateResult> {
  try {
    return await count("tokenConfig", { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting TokenConfig at path 'tokenConfig': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on TokenConfig items in collection tokenConfig.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregateTokenConfig(operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate("tokenConfig", operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating TokenConfig at path 'tokenConfig': ${formatError(error)}`);
    return { value: 0 };
  }
}

